import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import {
  AppShell,
  Select,
  SidebarNav,
  type SidebarNavSection,
} from "@legends/ui";
import {
  ApiError,
  fetchConfigurableCaves,
  fetchHealth,
  fetchHello,
  logout,
  type AuthUser,
  type AuthzMe,
  type Cave,
} from "./api";
import SecurityScreen from "./SecurityScreen";
import MetricsScreen from "./MetricsScreen";
import MarketingScreen from "./MarketingScreen";
import LegendsAdminScreen from "./LegendsAdminScreen";
import CavesAdminScreen from "./CavesAdminScreen";
import MediaLibraryScreen from "./admin/MediaLibraryScreen";
import {
  LegendsBrand,
  ShellTitle,
  SidebarUser,
} from "./sections";
// Admin-only redesign token layer (Phase 1). Scoped to the `admin-scope` class,
// which HomeScreen toggles on <body> while mounted (see useAdminScope) so it also
// covers portaled dialogs; other views never mount this screen, so they keep the
// Cave language. A documented, scoped exception like studioChrome (ADR 0087).
import "./admin/adminTheme.css";
import CaveDetailScreen, { CAVE_TABS, type CaveTab } from "./admin/CaveDetailScreen";
import AdminOverviewScreen from "./admin/AdminOverviewScreen";
import AuditScreen from "./admin/AuditScreen";
import CommandPalette, { type Command } from "./admin/CommandPalette";
import { caveBySlug, caveSlug } from "./admin/caveObject";

export interface HomeScreenProps {
  user: AuthUser;
  authz: AuthzMe;
  onLogout: () => void;
  /** Impersonation strip, rendered in the shell's banner slot (see App.tsx). */
  banner?: ReactNode;
}

type PortalView =
  | "uplink"
  | "metrics"
  | "marketing"
  | "legends"
  | "mapping"
  | "caves"
  | "rolls"
  | "board-slates"
  | "media"
  | "security"
  | "audit"
  | "events"
  | "tickets"
  | "recordings"
  | "entitlements";

const VIEW_LABEL: Record<PortalView, string> = {
  uplink: "Admin",
  metrics: "Metrics",
  marketing: "Marketing",
  legends: "Legends",
  mapping: "Mapping",
  caves: "Caves",
  rolls: "Pre/Post-roll",
  "board-slates": "Board slates",
  media: "Media",
  security: "Security",
  audit: "Activity",
  events: "Events",
  tickets: "Tickets",
  recordings: "Recordings",
  entitlements: "Entitlements",
};

// The signed-in admin-portal shell (ADR 0009): a top-bar tenant dropdown +
// a left menu (portal sections for super admins) drive a full-width content
// region — the hello-world uplink card, the Admin/Security/Impersonate
// consoles, or the cave Events/Tickets setup for the selected tenant. The user
// chip + Sign out live in the sidebar footer; the impersonation strip, if any,
// arrives via the banner prop.
// Derived from VIEW_LABEL so a new tab can never be missing from the URL
// allowlist (which would silently fall back to Uplink).
const PORTAL_VIEWS = Object.keys(VIEW_LABEL) as PortalView[];

export default function HomeScreen({
  user,
  authz,
  onLogout,
  banner,
}: HomeScreenProps) {
  // The active tab is the URL (/admin/:tab), so refresh keeps you on it and tabs
  // are deep-linkable (ADR 0021). "/" (or an unknown tab) → Uplink.
  const navigate = useNavigate();
  const location = useLocation();
  const tabParam = matchPath("/admin/:tab", location.pathname)?.params.tab;
  const view: PortalView = (PORTAL_VIEWS as string[]).includes(tabParam ?? "")
    ? (tabParam as PortalView)
    : "uplink";
  const setView = (v: PortalView) =>
    navigate(v === "uplink" ? "/admin" : `/admin/${v}`);
  // Cave object page (Phase 2): /admin/caves/:slug[/:tab]. Matched before the flat
  // tab logic — the caves LIST is /admin/caves; a further slug segment is the detail
  // page. A missing tab defaults to Overview.
  const caveDetailMatch =
    matchPath("/admin/caves/:slug/:caveTab", location.pathname) ??
    matchPath("/admin/caves/:slug", location.pathname);
  const onCaveDetail = !!caveDetailMatch;
  const openCave = (slug: string) => navigate(`/admin/caves/${slug}`);
  const openCaveTab = (slug: string, t: CaveTab) => navigate(`/admin/caves/${slug}/${t}`);
  const [tenantId, setTenantId] = useState<string | null>(
    authz.tenants[0]?.id ?? null,
  );
  const [, setMessage] = useState<string | null>(null);
  const [version, setVersion] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [denied, setDenied] = useState(false);
  // Every cave this super admin may configure (all caves). Fetched once; the
  // Events/Tickets sections scope it to the selected tenant.
  const [caves, setCaves] = useState<Cave[]>([]);

  // Apply the admin-only redesign token layer for as long as this screen is
  // mounted. Setting the class on <body> (not a wrapper div) means portaled
  // dialogs/drawers are covered too; removing it on unmount restores the Cave
  // language for any other view. Only super-admins mount HomeScreen, and they
  // only ever see the admin, so this is admin-scoped in practice.
  useEffect(() => {
    document.body.classList.add("admin-scope");
    return () => document.body.classList.remove("admin-scope");
  }, []);

  // Legacy cave-scoped routes now live on the cave object page, and Mapping
  // dissolved into the cave Overview readiness card — redirect old bookmarks (kept
  // for a release). Events/Tickets/Recordings/Entitlements land on the last-opened
  // cave (or the Caves list); /admin/mapping → the admin home.
  const movedTab: Record<string, CaveTab> = {
    events: "events",
    tickets: "tickets",
    recordings: "recordings",
    entitlements: "fans",
  };
  const redirecting = !!(tabParam && (tabParam in movedTab || tabParam === "mapping"));
  useEffect(() => {
    if (tabParam && tabParam in movedTab) {
      let last = "";
      try {
        last = localStorage.getItem("admin.lastCaveSlug") ?? "";
      } catch {
        /* blocked storage — fall back to the caves list */
      }
      navigate(last ? `/admin/caves/${last}/${movedTab[tabParam]}` : "/admin/caves", { replace: true });
    } else if (tabParam === "mapping") {
      navigate("/admin", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabParam, navigate]);

  const load = useCallback(() => {
    setMessage(null);
    setError(null);
    setDenied(false);
    Promise.all([fetchHello(tenantId ?? undefined), fetchHealth()])
      .then(([hello, health]) => {
        setMessage(hello.message);
        setVersion(health.version);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 403) {
          setDenied(true);
        } else {
          setError(err instanceof Error ? err.message : String(err));
        }
      });
  }, [tenantId]);

  useEffect(() => load(), [load]);

  // Refetch on the active tab too, so a cave just created in the Caves tab shows
  // up in the Events/Tickets selector without a full reload.
  useEffect(() => {
    if (!authz.superAdmin) return;
    fetchConfigurableCaves()
      .then(setCaves)
      .catch(() => setCaves([]));
  }, [authz.superAdmin, view]);

  const signOut = () => {
    logout().finally(onLogout);
  };

  const selectedTenant = authz.tenants.find((t) => t.id === tenantId);

  const sections: SidebarNavSection[] = [];
  if (authz.superAdmin) {
    // Phase 2: Events / Tickets / Recordings / Entitlements moved onto the cave
    // object page (/admin/caves/:slug) as tabs, and Mapping dissolved into the cave
    // Overview readiness card — so they leave the flat nav (legacy paths redirect).
    sections.push({
      items: [
        { label: "Caves", icon: "◈", active: view === "caves" || onCaveDetail, onClick: () => setView("caves") },
        { label: "Legends", icon: "★", active: view === "legends", onClick: () => setView("legends") },
        { label: "Metrics", icon: "▤", active: view === "metrics", onClick: () => setView("metrics") },
        { label: "Marketing", icon: "◇", active: view === "marketing", onClick: () => setView("marketing") },
        { label: "Media", icon: "▩", active: view === "media" || view === "rolls" || view === "board-slates", onClick: () => setView("media") },
        { label: "Security", icon: "⊟", active: view === "security", onClick: () => setView("security") },
        { label: "Activity", icon: "≣", active: view === "audit", onClick: () => setView("audit") },
      ],
    });
  }

  // Admin landing at bare /admin. This replaced a dev-stub "Uplink handshake" card
  // (bug #5: it showed "Legends · Uplink" / "Establishing link…" / a "Re-run
  // handshake" button echoing "Hello from legends" — a debug artifact reachable in
  // beta). Phase 3 of the redesign replaces this with the Overview attention queue
  // (caves that can't go live · failed recordings · abandoned checkouts).
  const uplinkCard = (
    <div style={{ maxWidth: 640 }}>
      <div
        style={{
          background: "var(--surface-2)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-lg)",
          padding: "24px",
        }}
      >
        <h1
          style={{
            margin: 0,
            fontFamily: "var(--font-display)",
            fontSize: "var(--fs-display)",
            fontWeight: 600,
            letterSpacing: "var(--ls-display)",
            textTransform: "uppercase",
            color: "var(--text-1)",
          }}
        >
          Admin console
        </h1>
        {denied ? (
          <p role="alert" style={{ margin: "12px 0 0", color: "var(--alert)", fontSize: "var(--fs-md)" }}>
            Access denied in {selectedTenant?.name ?? tenantId}.
          </p>
        ) : error ? (
          <p role="alert" style={{ margin: "12px 0 0", color: "var(--alert)", fontSize: "var(--fs-md)" }}>
            API unreachable: {error}
          </p>
        ) : (
          <p style={{ margin: "12px 0 0", color: "var(--text-2)", fontSize: "var(--fs-md)", lineHeight: 1.5 }}>
            Choose a section from the sidebar. A platform overview — the caves that
            can’t go live, failed recordings and abandoned checkouts — lands here soon.
          </p>
        )}
      </div>
    </div>
  );

  // The cave object page (Phase 2), resolved from the URL slug.
  let caveDetailContent: ReactNode = null;
  if (onCaveDetail) {
    const slug = caveDetailMatch?.params.slug ?? "";
    const rawTab = (caveDetailMatch?.params as { caveTab?: string } | undefined)?.caveTab ?? "overview";
    const caveTab: CaveTab = (CAVE_TABS as readonly string[]).includes(rawTab) ? (rawTab as CaveTab) : "overview";
    const cave = caveBySlug(caves, slug);
    caveDetailContent = !caves.length ? (
      <p style={{ color: "var(--text-3)", fontSize: "var(--fs-md)" }}>Loading…</p>
    ) : !cave ? (
      <p role="alert" style={{ color: "var(--live)", fontSize: "var(--fs-md)" }}>
        No cave “{slug}”.{" "}
        <button type="button" onClick={() => setView("caves")} style={{ background: "none", border: "none", color: "var(--text-2)", textDecoration: "underline", cursor: "pointer", font: "inherit", padding: 0 }}>
          Back to caves
        </button>
      </p>
    ) : (
      <CaveDetailScreen cave={cave} tab={caveTab} authz={authz} onOpenTab={openCaveTab} onOpenCaves={() => setView("caves")} />
    );
  }

  const content = onCaveDetail ? (
    caveDetailContent
  ) : redirecting ? null : view === "metrics" ? (
    <MetricsScreen />
  ) : view === "marketing" ? (
    <MarketingScreen />
  ) : view === "legends" ? (
    <LegendsAdminScreen />
  ) : view === "caves" ? (
    <CavesAdminScreen onOpenCave={(c) => openCave(caveSlug(c))} />
  ) : view === "media" || view === "rolls" || view === "board-slates" ? (
    <MediaLibraryScreen />
  ) : view === "security" ? (
    <SecurityScreen currentUsername={user.username} />
  ) : view === "audit" ? (
    <AuditScreen />
  ) : denied || error || !authz.superAdmin ? (
    uplinkCard
  ) : (
    <AdminOverviewScreen onOpenCave={openCave} onOpenAudit={() => setView("audit")} />
  );

  // ⌘K command palette (ADR 0160): every section + every cave, jumpable by name.
  const paletteCommands: Command[] = useMemo(() => {
    const sections: { view: PortalView; label: string }[] = [
      { view: "caves", label: "Caves" },
      { view: "legends", label: "Legends" },
      { view: "metrics", label: "Metrics" },
      { view: "marketing", label: "Marketing" },
      { view: "media", label: "Media" },
      { view: "security", label: "Security" },
      { view: "audit", label: "Activity" },
    ];
    const cmds: Command[] = sections.map((s) => ({
      id: `section-${s.view}`,
      label: s.label,
      hint: "Section",
      keywords: "go to open",
      run: () => setView(s.view),
    }));
    for (const c of caves) {
      cmds.push({
        id: `cave-${c.id}`,
        label: c.name,
        hint: "Cave",
        keywords: `${c.host} open cave`,
        run: () => openCave(caveSlug(c)),
      });
    }
    return cmds;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caves]);

  return (
    <AppShell
      brand={<LegendsBrand />}
      title={
        <ShellTitle>
          {onCaveDetail
            ? (caveBySlug(caves, caveDetailMatch?.params.slug ?? "")?.name ?? "Cave")
            : VIEW_LABEL[view]}
        </ShellTitle>
      }
      actions={
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "var(--fs-2xs)",
              letterSpacing: "var(--ls-label)",
              textTransform: "uppercase",
              color: "var(--text-dim)",
            }}
          >
            {version ? `API ${version}` : "API —"}
          </span>
          {authz.tenants.length > 0 && (
            <Select
              aria-label="Tenant"
              value={tenantId ?? ""}
              onChange={setTenantId}
              options={authz.tenants.map((t) => ({
                value: t.id,
                label: t.name,
              }))}
            />
          )}
        </div>
      }
      sidebar={<SidebarNav sections={sections} />}
      sidebarFooter={
        <SidebarUser
          name={user.displayName || user.username}
          onSignOut={signOut}
        />
      }
      banner={banner}
    >
      {content}
      {authz.superAdmin && <CommandPalette commands={paletteCommands} />}
    </AppShell>
  );
}
